import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { CheckCircle2, MapPin, Copy, CreditCard, Loader2, Package } from "lucide-react";
import { toast } from "sonner";
import Layout from "@/components/layout/Layout";
import api from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { useSiteMode } from "@/context/SiteModeContext"; // eslint-disable-line no-unused-vars
import { GreenButton, GhostButton } from "@/components/Buttons";
import { VerifiedShop } from "@/components/VerifiedShop";
import SimpleConfirmation from "@/pages/simple/SimpleConfirmation";

/** `/order-confirmation/:id` — Simple shows the 7.8 screen, Classic keeps its own. */
export default function OrderConfirmation() {
    return <SimpleConfirmation />;
}

function ClassicConfirmation() {
    const { id } = useParams();
    const settings = useSettings();
    const b = settings?.business;
    const [order, setOrder] = useState(null);
    const [paying, setPaying] = useState(false);

    useEffect(() => { api.get(`/orders/${id}`).then(({ data }) => setOrder(data)).catch(() => {}); }, [id]);

    const payByCard = async () => {
        setPaying(true);
        try {
            const { data } = await api.post("/payments/order/checkout", { order_id: id, origin_url: window.location.origin });
            window.location.href = data.checkout_url;
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not start the card payment");
            setPaying(false);
        }
    };

    const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied"); };
    const suppliedItem = order?.items?.some((i) => i.selections?.source === "supplied");

    if (!order) return <Layout hideTicker><div className="flex min-h-[60vh] items-center justify-center text-zinc-400"><Loader2 className="animate-spin" /></div></Layout>;

    return (
        <Layout hideTicker>
            <div className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
                <div className="rounded-3xl border border-brand-green/30 bg-brand-green/[0.06] p-8 text-center">
                    <CheckCircle2 size={48} className="mx-auto text-brand-green" />
                    <h1 className="mt-4 font-display text-3xl font-black">{order.card_paid ? "Paid — you're in production!" : "Order received!"}</h1>
                    <p className="mt-2 text-zinc-300">Order <span className="font-mono font-bold text-brand-green" data-testid="order-number">{order.order_number}</span></p>
                    <p className="mt-1 text-sm text-zinc-400">
                        {order.card_paid
                            ? "Card payment confirmed. We'll text you the moment it's ready for pickup."
                            : "We'll text you to confirm details & final pricing. Save this number."}
                    </p>
                    {order.card_paid && (
                        <p className="mt-3 inline-flex items-center gap-2 rounded-full border border-brand-green/50 bg-brand-green/10 px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.2em] text-brand-green" data-testid="order-paid-badge">
                            <CreditCard size={12} /> Paid ${(order.paid_amount || order.card_total || 0).toFixed(2)} by card
                        </p>
                    )}
                    <VerifiedShop className="mt-4" note="Sold & printed by us" testid="order-verified-shop" />
                </div>

                {order.proof_token && order.items?.some((i) => i.proof) && (
                    <div className="mt-6 rounded-3xl border border-brand-blue/30 bg-brand-blue/[0.08] p-6" data-testid="proof-banner">
                        <h2 className="font-display text-xl font-bold">Approve your proof</h2>
                        <p className="mt-1 text-sm text-zinc-300">We emailed a picture of your design exactly as you built it. One tap to approve and we print it.</p>
                        <Link to={`/proof/${order.proof_token}`} className="mt-4 inline-block" data-testid="proof-link">
                            <GreenButton>View & approve proof</GreenButton>
                        </Link>
                    </div>
                )}

                <div className="mt-6 rounded-3xl border border-white/10 bg-white/[0.03] p-6">
                    <h2 className="font-display text-xl font-bold">{order.card_paid ? "Payment received" : "Send payment to start"}</h2>
                    {!order.card_paid && (order.card_total || 0) >= 0.5 && (
                        <div className="mt-4 rounded-2xl border border-brand-green/40 bg-brand-green/[0.07] p-5" data-testid="order-pay-card-block">
                            <p className="text-sm text-zinc-200">
                                Skip the back and forth — pay <span className="font-bold text-brand-green">${order.card_total.toFixed(2)}</span> by
                                card now and your job goes straight into production.
                            </p>
                            <GreenButton className="mt-3" onClick={payByCard} disabled={paying} data-testid="order-pay-card">
                                {paying ? <Loader2 className="animate-spin" size={16} /> : <CreditCard size={16} />} Pay by card
                            </GreenButton>
                        </div>
                    )}
                    {!order.card_paid && <p className="mt-4 text-sm text-zinc-400">{b?.payment_note}</p>}
                    {!order.card_paid && (
                    <div className="mt-4 grid gap-3 sm:grid-cols-2">
                        {(b?.payment_methods || []).map((m) => (
                            <button key={m.type} onClick={() => copy(m.handle)} className="flex items-center justify-between rounded-xl border border-white/10 bg-brand-ink px-4 py-3 text-left transition-colors hover:border-brand-green/50">
                                <div>
                                    <div className="font-mono text-[11px] uppercase tracking-widest text-brand-green">{m.type}</div>
                                    <div className="font-semibold">{m.handle}</div>
                                </div>
                                <Copy size={15} className="text-zinc-500" />
                            </button>
                        ))}
                    </div>
                    )}
                    <div className="mt-4 flex items-end justify-between rounded-xl border border-white/10 bg-brand-ink px-4 py-3">
                        <span className="text-sm text-zinc-400">{order.card_paid ? "Order total" : "Estimated total"}</span>
                        <span className="font-display text-2xl font-black text-brand-green">${order.total.toFixed(2)}</span>
                    </div>
                    {order.member_discount > 0 && (
                        <p className="mt-2 text-xs text-brand-green" data-testid="order-member-discount">
                            Gang {order.member_tier_name} member discount applied — you saved ${order.member_discount.toFixed(2)} on this order.
                        </p>
                    )}
                    {order.coupon_discount > 0 && (
                        <p className="mt-2 text-xs text-zinc-300" data-testid="order-coupon">
                            Coupon <span className="font-mono font-bold text-white">{order.coupon_code}</span>
                            {order.coupon_title ? ` — ${order.coupon_title}` : ""} — ${order.coupon_discount.toFixed(2)} off.
                        </p>
                    )}
                </div>

                <div className="mt-6 grid gap-4 sm:grid-cols-2">
                    <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6">
                        <Package className="text-brand-green" />
                        <h3 className="mt-3 font-display text-lg font-bold">Pickup</h3>
                        <p className="mt-1 text-sm text-zinc-300">{b?.pickup?.address}</p>
                        <p className="mt-2 text-xs text-zinc-500">{b?.pickup?.note}</p>
                        <p className="mt-2 text-xs text-zinc-500">{b?.turnaround_note}</p>
                    </div>
                    <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6">
                        <MapPin className="text-brand-blue" />
                        <h3 className="mt-3 font-display text-lg font-bold">{suppliedItem ? "Dropping off blanks?" : "Where we are"}</h3>
                        {suppliedItem ? (
                            <ol className="mt-2 list-decimal space-y-1 pl-4 text-sm text-zinc-300" data-testid="dropoff-steps">
                                {(b?.dropoff?.steps || []).map((s, i) => (
                                    <li key={i}>
                                        {order?.dropoff_code && /drop-off code/i.test(s)
                                            ? <>Enter the drop-off code: <span className="font-mono font-bold text-white" data-testid="dropoff-code">{order.dropoff_code}</span></>
                                            : s}
                                    </li>
                                ))}
                            </ol>
                        ) : (
                            <p className="mt-1 text-sm text-zinc-300">{b?.address}. Self-serve 24-hour lockers — no walk-ins.</p>
                        )}
                    </div>
                </div>

                <div className="mt-8 flex flex-wrap justify-center gap-3">
                    <Link to="/directory"><GhostButton>Back home</GhostButton></Link>
                    <Link to="/shirts/white"><GreenButton>Order more</GreenButton></Link>
                </div>
            </div>
        </Layout>
    );
}
